import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';

const SIZE_OPTIONS = [
  { value: 'calls', label: 'Calls' },
  { value: 'analyst_count', label: 'Analysts' },
];

const COLOR_OPTIONS = [
  { value: 'max', label: 'Max Return' },
  { value: '7d', label: '7D Return' },
  { value: '1m', label: '1M Return' },
  { value: '3m', label: '3M Return' },
  { value: '6m', label: '6M Return' },
  { value: '1y', label: '1Y Return' },
];

// Squarified treemap layout algorithm
function squarify(items, x, y, w, h, out) {
  if (!items.length) return;
  if (items.length === 1) {
    out.push({ ...items[0], x, y, w, h });
    return;
  }

  const total = items.reduce((s, it) => s + it._area, 0);
  const shortSide = Math.min(w, h);
  const horizontal = w >= h;

  let row = [];
  let rowArea = 0;
  let i = 0;

  while (i < items.length) {
    const candidate = items[i];
    const newArea = rowArea + candidate._area;
    if (row.length === 0 || worst(row, shortSide, rowArea) >= worst([...row, candidate], shortSide, newArea)) {
      row.push(candidate);
      rowArea = newArea;
      i++;
    } else {
      break;
    }
  }

  const rowLen = rowArea / shortSide;
  let cursor = horizontal ? y : x;

  for (const item of row) {
    const itemLen = (item._area / rowArea) * shortSide;
    if (horizontal) {
      out.push({ ...item, x, y: cursor, w: rowLen, h: itemLen });
    } else {
      out.push({ ...item, x: cursor, y, w: itemLen, h: rowLen });
    }
    cursor += itemLen;
  }

  if (i < items.length) {
    if (horizontal) {
      squarify(items.slice(i), x + rowLen, y, w - rowLen, h, out);
    } else {
      squarify(items.slice(i), x, y + rowLen, w, h - rowLen, out);
    }
  }
}

function worst(row, side, totalArea) {
  const len = totalArea / side;
  return Math.max(...row.map((it) => {
    const h = it._area / len;
    return Math.max(len / h, h / len);
  }));
}

function blendHex(hex1, hex2, t) {
  const parse = (h) => [
    parseInt(h.slice(1, 3), 16),
    parseInt(h.slice(3, 5), 16),
    parseInt(h.slice(5, 7), 16),
  ];
  const [r1, g1, b1] = parse(hex1);
  const [r2, g2, b2] = parse(hex2);
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);
  return `rgb(${r},${g},${b})`;
}

function getColor(value, allValues) {
  if (value == null) return '#e5e7eb';
  const GREEN = '#10b981';
  const GRAY = '#9ca3af';
  const RED = '#ef4444';

  if (value === 0) return GRAY;

  const valid = allValues.filter((v) => v != null);
  if (!valid.length) return GRAY;

  const maxAbs = Math.max(...valid.map(Math.abs));
  if (maxAbs === 0) return GRAY;

  const t = Math.min(Math.abs(value) / maxAbs, 1);
  return value > 0 ? blendHex(GRAY, GREEN, t) : blendHex(GRAY, RED, t);
}

export default function SectorMapTab({ onSectorClick }) {
  const [rows, setRows] = useState([]);
  const [level, setLevel] = useState('sector');
  const [sizeMetric, setSizeMetric] = useState('calls');
  const [colorPeriod, setColorPeriod] = useState('max');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [tooltip, setTooltip] = useState(null);
  const containerRef = useRef(null);
  const [dims, setDims] = useState({ w: 700, h: 400 });

  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setDims({ w: width, h: Math.max(200, height) });
    });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { level, period: colorPeriod };
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    axios
      .get(`${API_BASE_URL}/api/research/sector-map/`, { params })
      .then((res) => setRows(res.data.rows ?? []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [level, colorPeriod, dateFrom, dateTo]);

  const layout = useMemo(() => {
    if (!rows.length || !dims.w) return [];
    const totalArea = dims.w * dims.h;
    const totalSize = rows.reduce((s, r) => s + (r[sizeMetric] || 0), 0) || 1;
    const items = rows.map((r) => ({
      ...r,
      _area: ((r[sizeMetric] || 0) / totalSize) * totalArea,
    })).filter((r) => r._area > 0);
    const out = [];
    squarify(items, 0, 0, dims.w, dims.h, out);
    return out;
  }, [rows, dims, sizeMetric]);

  const colorValues = rows.map((r) => r.avg_return);

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2 mb-4 flex-shrink-0">
        {/* Level toggle */}
        <div className="flex gap-1">
          {['sector', 'industry'].map((l) => (
            <button
              key={l}
              onClick={() => setLevel(l)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors capitalize ${
                level === l
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
              }`}
            >
              {l}
            </button>
          ))}
        </div>

        {/* Date range — hidden on mobile to save space, accessible via sm+ */}
        <div className="hidden sm:flex items-center gap-1">
          <span className="text-xs text-gray-400">From:</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="text-xs px-2 py-1.5 border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900"
          />
        </div>
        <div className="hidden sm:flex items-center gap-1">
          <span className="text-xs text-gray-400">To:</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="text-xs px-2 py-1.5 border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900"
          />
        </div>
        {(dateFrom || dateTo) && (
          <button
            onClick={() => { setDateFrom(''); setDateTo(''); }}
            className="hidden sm:block text-xs text-gray-400 hover:text-gray-700 px-1"
          >
            ✕ clear
          </button>
        )}

        <div className="flex items-center gap-2 ml-auto flex-wrap">
          {/* Size */}
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-400">Size:</span>
            <select
              value={sizeMetric}
              onChange={(e) => setSizeMetric(e.target.value)}
              className="text-xs px-2 py-1.5 border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900"
            >
              {SIZE_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          {/* Color — return period */}
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-400">Color:</span>
            <select
              value={colorPeriod}
              onChange={(e) => setColorPeriod(e.target.value)}
              className="text-xs px-2 py-1.5 border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900"
            >
              {COLOR_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Treemap */}
      <div
        ref={containerRef}
        className="relative bg-gray-100 rounded-xl overflow-hidden flex-1 min-h-0"
      >
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm animate-pulse">
            Loading sector map…
          </div>
        ) : layout.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm">
            No data yet.
          </div>
        ) : (
          layout.map((item) => {
            const bg = getColor(item.avg_return, colorValues);
            const tooSmall = item.w < 40 || item.h < 30;
            return (
              <div
                key={item.label}
                className="absolute cursor-pointer hover:brightness-110 transition-all border border-white/30"
                style={{
                  left: item.x,
                  top: item.y,
                  width: item.w,
                  height: item.h,
                  backgroundColor: bg,
                }}
                onMouseEnter={(e) =>
                  setTooltip({
                    label: item.label,
                    calls: item.calls,
                    analyst_count: item.analyst_count,
                    avg_return: item.avg_return,
                    x: e.clientX,
                    y: e.clientY,
                  })
                }
                onMouseMove={(e) =>
                  setTooltip((t) => t ? { ...t, x: e.clientX, y: e.clientY } : t)
                }
                onMouseLeave={() => setTooltip(null)}
                onTouchStart={(e) => {
                  e.stopPropagation();
                  const touch = e.touches[0];
                  setTooltip({
                    label: item.label,
                    calls: item.calls,
                    analyst_count: item.analyst_count,
                    avg_return: item.avg_return,
                    x: touch.clientX,
                    y: touch.clientY,
                  });
                }}
                onTouchEnd={() => setTimeout(() => setTooltip(null), 1500)}
                onClick={() => onSectorClick && onSectorClick({ label: item.label, level })}
              >
                {!tooSmall && (
                  <div className="p-1.5 h-full flex flex-col justify-between overflow-hidden">
                    <span
                      className="text-white font-semibold leading-tight"
                      style={{ fontSize: Math.min(13, Math.max(9, item.w / 10)) }}
                    >
                      {item.label}
                    </span>
                    {item.h > 50 && (
                      <span className="text-white/80 text-[9px]">
                        {item.calls} calls
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}

        {/* Tooltip */}
        {tooltip && (
          <div
            className="fixed z-50 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl pointer-events-none"
            style={{ left: tooltip.x + 12, top: tooltip.y - 10 }}
          >
            <div className="font-semibold mb-1">{tooltip.label}</div>
            <div className="text-gray-300 space-y-0.5">
              <div>{tooltip.calls} calls · {tooltip.analyst_count} analysts</div>
              <div>
                Return ({COLOR_OPTIONS.find((o) => o.value === colorPeriod)?.label}):{' '}
                {tooltip.avg_return != null
                  ? `${tooltip.avg_return >= 0 ? '+' : ''}${tooltip.avg_return.toFixed(1)}%`
                  : '—'}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 mt-3 text-xs text-gray-400 flex-shrink-0">
        <span>Color: {COLOR_OPTIONS.find((o) => o.value === colorPeriod)?.label}</span>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-sm bg-red-400" />Negative
          <div className="w-3 h-3 rounded-sm bg-gray-400 ml-1" />Zero
          <div className="w-3 h-3 rounded-sm bg-emerald-500 ml-1" />Positive
        </div>
        <span className="ml-auto">Size: {SIZE_OPTIONS.find((o) => o.value === sizeMetric)?.label}</span>
      </div>
    </div>
  );
}
